/**
 * Pixel API tracking utilities for offer impression tracking
 * Manages localStorage-based tracking with cooldown periods to prevent duplicate tracking
 */

import { spotlight_mobile } from "@/generated";
import { light_offer_extended } from "@/lib/models/light_offer_extended";

const PIXEL_TRACKING_KEY = "welcia_pixel_tracking";
const PIXEL_COOLDOWN_MINUTES = 30;
const DEBUG = process.env.NODE_ENV === "development";

const offerKey = (offerId: number, userId?: number) =>
  `${userId || "guest"}_${offerId}`;

const spotlightKey = (spotlightId: number, userId?: number) =>
  `${userId || "guest"}_spotlight_${spotlightId}`;

const hasKeyBeenTracked = (key: string): boolean => {
  try {
    const trackingData = localStorage.getItem(PIXEL_TRACKING_KEY);
    if (!trackingData) return false;

    const tracking = JSON.parse(trackingData);
    const lastTracked = tracking[key];

    if (!lastTracked) return false;

    const cooldownMs = PIXEL_COOLDOWN_MINUTES * 60 * 1000;
    return Date.now() - lastTracked < cooldownMs;
  } catch (error) {
    if (DEBUG) {
      console.error("Error checking pixel tracking status:", error);
    }
    return false;
  }
};

const markKeyAsTracked = (key: string): void => {
  try {
    const trackingData = localStorage.getItem(PIXEL_TRACKING_KEY);
    const tracking = trackingData ? JSON.parse(trackingData) : {};

    tracking[key] = Date.now();

    // Clean up old entries (older than 24 hours)
    const now = Date.now();
    const cleanupMs = 24 * 60 * 60 * 1000;

    Object.keys(tracking).forEach((k) => {
      if (now - tracking[k] > cleanupMs) {
        delete tracking[k];
      }
    });

    localStorage.setItem(PIXEL_TRACKING_KEY, JSON.stringify(tracking));
  } catch (error) {
    if (DEBUG) {
      console.error("Error marking pixel tracking entry:", error);
    }
  }
};

/**
 * Check if offer has been tracked within the cooldown period for a specific user
 */
export const hasOfferBeenTracked = (
  offerId: number,
  userId?: number,
): boolean => hasKeyBeenTracked(offerKey(offerId, userId));

/**
 * Mark offer as tracked for a specific user
 */
export const markOfferAsTracked = (offerId: number, userId?: number): void =>
  markKeyAsTracked(offerKey(offerId, userId));

/**
 * Check if spotlight banner has been tracked within the cooldown period for a specific user
 */
export const hasSpotlightBeenTracked = (
  spotlightId: number,
  userId?: number,
): boolean => hasKeyBeenTracked(spotlightKey(spotlightId, userId));

/**
 * Mark spotlight banner as tracked for a specific user
 */
export const markSpotlightAsTracked = (
  spotlightId: number,
  userId?: number,
): void => markKeyAsTracked(spotlightKey(spotlightId, userId));

/**
 * Interface for pixel API tracking data
 */
export interface PixelTrackingData {
  offerId?: number; // Optional offer ID for tracking specific offers
  memberId?: number;
  holderRef?: string;
  retailerId: number;
  storeId?: number;
  offer?: light_offer_extended;
  spotlight?: spotlight_mobile;
}
